import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Trash2, Search, Building2, Users, X, UserCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { branchService, userService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { canDelete, isSuperAdmin } from '../../utils/rolePermissions';

const BranchMaster = () => {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [assigningBranch, setAssigningBranch] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  useEffect(() => {
    loadBranches();
    loadEmployees();
  }, []);

  const loadBranches = async () => {
    try {
      setLoading(true);
      const response = await branchService.getAll({ search: searchTerm });
      // Load employees for each branch
      const branchesWithUsers = (response.data || []).map(branch => {
        // Fetch users for this branch
        return {
          ...branch,
          users: branch.users || []
        };
      });
      setBranches(branchesWithUsers);
    } catch (error) {
      console.error('Error loading branches:', error);
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadBranches();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const openModal = (branch = null) => {
    setEditingBranch(branch);
    setShowModal(true);
    if (branch) {
      reset({
        branch_name: branch.branch_name,
        branch_description: branch.branch_description || '',
        branch_full_address: branch.branch_full_address,
        city: branch.city,
        state: branch.state,
        country: branch.country,
        pincode: branch.pincode,
        status: branch.status ? 1 : 0
      });
    } else {
      reset({ 
        branch_name: '', 
        branch_description: '', 
        branch_full_address: '', 
        city: '', 
        state: '', 
        country: '', 
        pincode: '', 
        status: 1 
      });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBranch(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const submitData = {
        ...data,
        status: data.status === 1 || data.status === true
      };
      
      if (editingBranch) {
        await branchService.update(editingBranch.id, submitData);
        toast.success('Branch updated successfully!');
      } else {
        await branchService.create(submitData);
        toast.success('Branch created successfully!');
      }
      closeModal();
      loadBranches();
    } catch (error) {
      console.error('Error saving branch:', error);
      toast.error('Failed to save branch');
    } finally {
      setLoading(false);
    }
  };

  const deleteBranch = async (id) => {
    if (isSuperAdmin(user)) {
      toast.error('Super Admin cannot delete records');
      return;
    }

    if (window.confirm('Are you sure you want to delete this branch?')) {
      try {
        setLoading(true);
        await branchService.delete(id);
        toast.success('Branch deleted successfully!');
        loadBranches();
      } catch (error) {
        console.error('Error deleting branch:', error);
        toast.error('Failed to delete branch');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleStatus = async (branch) => {
    try {
      setLoading(true);
      await branchService.update(branch.id, { 
        branch_name: branch.branch_name,
        branch_description: branch.branch_description,
        branch_full_address: branch.branch_full_address,
        city: branch.city,
        state: branch.state,
        country: branch.country,
        pincode: branch.pincode,
        status: !branch.status 
      });
      toast.success('Branch status updated!');
      loadBranches();
    } catch (error) {
      console.error('Error updating branch status:', error);
      toast.error('Failed to update branch status');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await userService.getAll({ per_page: 1000 });
      const employeesData = response.data || [];
      console.log('Loaded employees:', employeesData);
      // Log employees with their roles
      employeesData.forEach(emp => {
        console.log(`Employee: ${emp.name}, Role: ${emp.role}, RoleModel:`, emp.roleModel);
      });
      setEmployees(employeesData);
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Failed to load employees');
    }
  };

  const openEmployeeModal = (branch) => {
    setAssigningBranch(branch);
    // Get already assigned employee IDs for this branch
    const assignedIds = branch.users?.map(u => u.id) || [];
    setSelectedEmployees(assignedIds);
    setShowEmployeeModal(true);
  };

  const closeEmployeeModal = () => {
    setShowEmployeeModal(false);
    setAssigningBranch(null);
    setSelectedEmployees([]);
  };

  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleAssignEmployees = async () => {
    if (!assigningBranch) return;

    try {
      setLoading(true);
      // Update all employees to include this branch in their assignments
      // First, get current branch assignments for selected employees
      for (const employeeId of selectedEmployees) {
        const employee = employees.find(emp => emp.id === employeeId);
        if (employee) {
          // Get current branch IDs
          const currentBranchIds = employee.branches?.map(b => b.id) || [];
          // Check if this branch is already assigned
          if (!currentBranchIds.includes(assigningBranch.id)) {
            // Add this branch to the employee's branches
            const updatedBranchIds = [...currentBranchIds, assigningBranch.id];
            await userService.assignBranches(employeeId, { branch_ids: updatedBranchIds });
          }
        }
      }
      
      toast.success('Employees assigned to branch successfully!');
      closeEmployeeModal();
      loadBranches(); // Reload to get updated user data
    } catch (error) {
      console.error('Error assigning employees:', error);
      toast.error('Failed to assign employees');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
            Branch Master
          </h1>
          <button
            onClick={() => openModal()}
            className="btn btn-primary"
          >
            <Plus size={18} />
            Add Branch
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }}
          />
          <input
            type="text"
            placeholder="Search branches..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-box"
            style={{ paddingLeft: '40px' }}
          />
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Branch Name</th>
                <th>City</th>
                <th>State</th>
                <th>Assigned Employees</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((branch) => (
                <tr key={branch.id}>
                  <td>{branch.id}</td>
                  <td style={{ fontWeight: '500' }}>{branch.branch_name}</td>
                  <td>{branch.city}</td>
                  <td>{branch.state}</td>
                  <td>
                    {branch.users && branch.users.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                        {branch.users.slice(0, 2).map((user) => (
                          <span
                            key={user.id}
                            style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              backgroundColor: '#e0e7ff',
                              color: '#3730a3',
                              border: '1px solid #c7d2fe'
                            }}
                          >
                            {user.name}
                          </span>
                        ))}
                        {branch.users.length > 2 && (
                          <span
                            style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              backgroundColor: '#f3f4f6',
                              color: '#6b7280'
                            }}
                          >
                            +{branch.users.length - 2} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '13px' }}>No employees assigned</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => toggleStatus(branch)}
                      className={`status-badge ${branch.status ? 'status-active' : 'status-inactive'}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                      disabled={loading}
                    >
                      {branch.status ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => openEmployeeModal(branch)}
                        className="btn btn-secondary"
                        style={{ 
                          padding: '6px 8px', 
                          fontSize: '12px',
                          backgroundColor: '#4f46e5',
                          color: 'white'
                        }}
                        title="Assign Employees"
                      >
                        <Users size={14} />
                      </button>
                      <button
                        onClick={() => openModal(branch)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                      >
                        <Edit size={14} />
                      </button>
                      {canDelete(user) && (
                        <button
                          onClick={() => deleteBranch(branch.id)}
                          className="btn btn-danger"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          disabled={loading}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && branches.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            Loading branches...
          </div>
        )}

        {!loading && branches.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            No branches found.
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingBranch ? 'Edit Branch' : 'Add New Branch'}
              </h2>
              <button onClick={closeModal} className="close-btn">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Branch Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter branch name"
                    {...register('branch_name', {
                      required: 'Branch name is required',
                      minLength: {
                        value: 2,
                        message: 'Branch name must be at least 2 characters'
                      }
                    })}
                  />
                  {errors.branch_name && (
                    <div className="error-message">{errors.branch_name.message}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">City *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter city"
                    {...register('city', {
                      required: 'City is required'
                    })}
                  />
                  {errors.city && (
                    <div className="error-message">{errors.city.message}</div>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">State *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter state"
                    {...register('state', {
                      required: 'State is required'
                    })}
                  />
                  {errors.state && (
                    <div className="error-message">{errors.state.message}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Country *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter country"
                    {...register('country', {
                      required: 'Country is required'
                    })}
                  />
                  {errors.country && (
                    <div className="error-message">{errors.country.message}</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Full Address *</label>
                <textarea
                  className="form-input"
                  placeholder="Enter full address"
                  rows={3}
                  {...register('branch_full_address', {
                    required: 'Full address is required'
                  })}
                />
                {errors.branch_full_address && (
                  <div className="error-message">{errors.branch_full_address.message}</div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Pincode *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter pincode"
                    {...register('pincode', {
                      required: 'Pincode is required',
                      pattern: {
                        value: /^[0-9]{4,10}$/,
                        message: 'Pincode must be 4-10 digits'
                      }
                    })}
                  />
                  {errors.pincode && (
                    <div className="error-message">{errors.pincode.message}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    {...register('status', { required: 'Status is required' })}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                  {errors.status && (
                    <div className="error-message">{errors.status.message}</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  placeholder="Enter branch description (optional)"
                  rows={3}
                  {...register('branch_description')}
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingBranch ? 'Update Branch' : 'Create Branch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Employee Assignment Modal */}
      {showEmployeeModal && assigningBranch && (
        <div className="modal-overlay" onClick={closeEmployeeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                <Users size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Assign Employees to {assigningBranch.branch_name}
              </h2>
              <button onClick={closeEmployeeModal} className="close-btn">
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
              <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                Select employees to assign to this branch. Only authenticated employees with role "Store Manager" or "Authenticator" are shown.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {employees
                  .filter(emp => {
                    const roleName = emp.role || emp.roleModel?.name;
                    return roleName === 'Store Manager' || roleName === 'Authenticator';
                  })
                  .map((employee) => (
                    <label
                      key={employee.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        backgroundColor: selectedEmployees.includes(employee.id) ? '#eff6ff' : 'white',
                        transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(employee.id)}
                        onChange={() => handleEmployeeToggle(employee.id)}
                        style={{
                          marginRight: '12px',
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: '#111827' }}>{employee.name}</div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {employee.email} • {employee.role || employee.roleModel?.name || 'N/A'}
                        </div>
                      </div>
                      <UserCheck 
                        size={20} 
                        style={{ 
                          color: selectedEmployees.includes(employee.id) ? '#4f46e5' : '#d1d5db' 
                        }} 
                      />
                    </label>
                  ))}
              </div>

              {employees.filter(emp => {
                const roleName = emp.role || emp.roleModel?.name;
                return roleName === 'Store Manager' || roleName === 'Authenticator';
              }).length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  color: '#6b7280' 
                }}>
                  No Store Managers or Authenticators found.
                </div>
              )}
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={closeEmployeeModal}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssignEmployees}
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Assigning...' : 'Assign Employees'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchMaster;
